/**
 * Ties the signed-in session to the life of the browser session.
 *
 * The session used to outlive the app completely: the refresh token sat in
 * localStorage behind a seven-day cookie, so closing Xlog3D and coming back
 * later walked straight past the login screen.
 *
 * sessionStorage on its own cannot fix that. It is scoped to a TAB, not to the
 * browser session, so a second tab looked signed out and bounced a genuinely
 * signed-in user back to the login form — which is exactly why these flags were
 * moved to localStorage in the first place. Reverting that would restore the
 * old bug.
 *
 * So the app writes a heartbeat while it is open, including one last beat as it
 * closes. On launch, a heartbeat older than the grace window means no tab was
 * alive: the browser was quit, or the last Xlog3D tab was closed. A recent
 * heartbeat means another tab is running right now, so a newly opened tab
 * correctly inherits the session.
 */
const LAST_SEEN_KEY = '3dkey_last_seen';

const BEAT_MS = 10000;

/**
 * Comfortably longer than one beat. A reload, a slow launch or a backgrounded
 * tab must never be mistaken for the app having been closed; only a real
 * absence is. The cost is that quitting and returning within this window keeps
 * you signed in, which is the forgiving side to err on.
 */
const GRACE_MS = 45000;

function readLastSeen() {
  try {
    const raw = localStorage.getItem(LAST_SEEN_KEY);
    if (!raw) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch (e) {
    return null;
  }
}

/** Records that the app is alive right now. */
export function touchSession() {
  try {
    localStorage.setItem(LAST_SEEN_KEY, String(Date.now()));
  } catch (e) {
    // Private browsing and full quotas both throw; a missing heartbeat only
    // costs the user an extra sign-in, so it is never worth breaking on.
  }
}

/**
 * True when the app was running recently enough that this launch belongs to the
 * same browser session.
 *
 * A missing heartbeat reads as false: either nobody has signed in on this
 * browser, or storage was cleared. Both should land on the login screen.
 */
export function isBrowserSessionAlive() {
  const lastSeen = readLastSeen();
  if (lastSeen === null) return false;
  // A clock moved backwards would otherwise strand the user; treat the future
  // as "just seen" rather than "long gone".
  const age = Date.now() - lastSeen;
  return age <= GRACE_MS;
}

/** Forgets the heartbeat, so the next launch starts signed out. */
export function endBrowserSession() {
  try {
    localStorage.removeItem(LAST_SEEN_KEY);
  } catch (e) {
    // ignore
  }
}

/**
 * Beats while the app is open and returns a stop function.
 *
 * `pagehide` fires on tab close, navigation away and mobile app switching,
 * where `beforeunload` is unreliable — that final beat is what makes "closed
 * two minutes ago" distinguishable from "closed two seconds ago".
 */
export function startSessionHeartbeat() {
  touchSession();

  const timer = setInterval(touchSession, BEAT_MS);
  const onWake = () => {
    if (document.visibilityState === 'visible') touchSession();
  };

  window.addEventListener('pagehide', touchSession);
  document.addEventListener('visibilitychange', onWake);

  return () => {
    clearInterval(timer);
    window.removeEventListener('pagehide', touchSession);
    document.removeEventListener('visibilitychange', onWake);
  };
}

export const SESSION_GRACE_MS = GRACE_MS;
